import React, { useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Image,
  Text,
  VStack,
  Container,
  Button,
  useColorModeValue,
  Tag,
  Flex,
  Input,
  Select,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import glass1 from "../../../assets/images/glass1.jpg";
import glass2 from "../../../assets/images/glass2.jpg";
import glass3 from "../../../assets/images/glass3.jpg";
import glass4 from "../../../assets/images/glass4.jpg";
import Header from "../header/Header";
import { useHomeHook } from "../home/useHomeHook";

const MotionBox = motion(Box);
const MotionImage = motion(Image);

const glassItems = [
  {
    src: glass1,
    alt: "Tempered Glass",
    title: "Tempered Glass",
    description: "Durable and resistant to heat and impact.",
    category: "Safety Glass",
    price: "$50/sqft",
    thickness: "10mm",
    downloadPdf: "/path/to/tempered-glass-specs.pdf",
  },
  {
    src: glass2,
    alt: "Laminated Glass",
    title: "Laminated Glass",
    description: "Safety glass that holds together when shattered.",
    category: "Safety Glass",
    price: "$65/sqft",
    thickness: "12mm",
    downloadPdf: "/path/to/laminated-glass-specs.pdf",
  },
  {
    src: glass3,
    alt: "Frosted Glass",
    title: "Frosted Glass",
    description: "Provides privacy while allowing light to pass through.",
    category: "Decorative",
    price: "$45/sqft",
    thickness: "8mm",
    downloadPdf: "/path/to/frosted-glass-specs.pdf",
  },
  {
    src: glass4,
    alt: "Decorative Glass",
    title: "Decorative Glass",
    description: "Enhances the aesthetic of any space.",
    category: "Decorative",
    price: "$75/sqft",
    thickness: "10mm",
    downloadPdf: "/path/to/decorative-glass-specs.pdf",
  },
];

const Catelogs = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);
  // const bgColor = useColorModeValue('white', '#000000');
  // const borderColor = useColorModeValue('#000000', '#000000');
  const bgGradient = useColorModeValue(
    "linear(to-br, #000000, #87CEEB)",
    "linear(to-br, #000000, #87CEEB)"
  );
  const cardBg = useColorModeValue("white", "#000000");
  const textColor = useColorModeValue("#000000", "#000000");
  const headingColor = useColorModeValue("#87CEEB", "#87CEEB");
  const [handleMenuEnter, handleMenuLeave, menuVisible, setHoveredItem] =
    useHomeHook(props);
  const handleImageClick = (item) => {
    setSelectedImage(item);
    onOpen();
  };
  const filteredItems = glassItems.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      !selectedCategory || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  const categories = [...new Set(glassItems.map((item) => item.category))];
  return (
    <Box bgGradient={bgGradient} py={12}>
      <Header
        handleMenuEnter={handleMenuEnter}
        handleMenuLeave={handleMenuLeave}
        menuVisible={menuVisible}
        setHoveredItem={setHoveredItem}
        collections={[
          { name: "Sliding Partitions", path: "/product/1" },
          { name: "Swing Partitions", path: "/product/2" },
          // { name: "Flush Doors", path: "/product/3" },
          { name: "Bathroom & Wardrobes", path: "/product/4" },
          { name: "Shower Enclosures", path: "/product/5" },
          { name: "Led Mirrors", path: "/product/6" },
        ]}
      />
      <Container maxW="8xl" mt={16}>
        <VStack spacing={8} mb={12}>
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            textAlign="center"
            w="full"
          >
            <Heading
              as="h1"
              size="2xl"
              mb={4}
              bgGradient="linear(to-r, #87CEEB, #800080)"
              bgClip="text"
              letterSpacing="tight"
            >
              Glass Collection Catalog
            </Heading>
            <Text fontSize="xl" color={textColor} maxW="2xl" mx="auto">
              Explore our premium selection of glass solutions for your space
            </Text>
          </MotionBox>
          <Flex
            w="100%"
            gap={4}
            direction={{ base: "column", md: "row" }}
            justify="center"
            align="center"
            maxW="3xl"
            mx="auto"
          >
            <Flex position="relative" flex={1}>
              <Input
                placeholder="Search glass products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="lg"
                bg={cardBg}
                borderRadius="xl"
                boxShadow="sm"
                _focus={{
                  boxShadow: "outline",
                  borderColor: "#87CEEB",
                }}
                pr="48px"
              />
              <IconButton
                icon={<SearchIcon />}
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                aria-label="Search"
                variant="ghost"
                color="#87CEEB"
                _hover={{ bg: "#87CEEB" }}
              />
            </Flex>
            <Select
              placeholder="Filter by category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              size="lg"
              maxW={{ base: "100%", md: "200px" }}
              bg={cardBg}
              borderRadius="xl"
              boxShadow="sm"
              _focus={{
                boxShadow: "outline",
                borderColor: "#87CEEB",
              }}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
          </Flex>
        </VStack>
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={8}
          px={{ base: 4, lg: 0 }}
        >
          {filteredItems.map((item, index) => (
            <MotionBox
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              boxShadow="xl"
              _hover={{
                transform: "translateY(-8px)",
                boxShadow: "2xl",
              }}
            >
              <Box position="relative" overflow="hidden">
                <MotionBox
                  onClick={() => handleImageClick(item)}
                  cursor="pointer"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Image
                    src={item.src}
                    alt={item.alt}
                    objectFit="cover"
                    height="250px"
                    width="100%"
                  />
                </MotionBox>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="blackAlpha.700"
                  color="white"
                  p={4}
                  transform="translateY(100%)"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: "translateY(0)" }}
                >
                  <Text fontSize="sm">Click to view details</Text>
                </Box>
              </Box>
              <VStack p={6} align="start" spacing={4}>
                <Flex justify="space-between" width="100%" align="center">
                  <Heading as="h3" size="md" color={headingColor}>
                    {item.title}
                  </Heading>
                  <Tag colorScheme="blue" size="lg" borderRadius="full">
                    {item.category}
                  </Tag>
                </Flex>
                <Text color={textColor} fontSize="md">
                  {item.description}
                </Text>
                <Flex wrap="wrap" gap={3}>
                  <Tag size="md" colorScheme="purple" borderRadius="full">
                    {item.thickness}
                  </Tag>
                  <Tag size="md" colorScheme="green" borderRadius="full">
                    {item.price}
                  </Tag>
                </Flex>
                <Button
                  leftIcon={<DownloadIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  width="full"
                  borderRadius="xl"
                  onClick={() => window.open(item.downloadPdf)}
                  _hover={{
                    bg: "#87CEEB",
                    transform: "translateY(-2px)",
                  }}
                >
                  Download Specifications
                </Button>
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay backdropFilter="blur(8px)" />
          <ModalContent bg="transparent" boxShadow="none">
            <ModalCloseButton
              color="white"
              size="sm"
              bg="blackAlpha.600"
              borderRadius="full"
            />
            <ModalBody p={0}>
              {selectedImage && (
                <MotionImage
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  borderRadius="2xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default Catelogs;
